// Cookie management utilities for GDPR compliance

export interface CookieOptions {
  expires?: number // Days until expiration
  path?: string
  domain?: string
  secure?: boolean
  sameSite?: 'strict' | 'lax' | 'none'
}

export interface CookieConsent {
  necessary: boolean // Always true, required for basic functionality
  analytics: boolean
  functional: boolean
  timestamp: string
  version: string
}

const CONSENT_COOKIE_NAME = 'gdpr_consent'
const CONSENT_VERSION = '1.0'

export class CookieManager {
  /**
   * Set a cookie with GDPR-compliant options
   */
  static set(name: string, value: string, options: CookieOptions = {}) {
    const {
      expires = 365, // Default to 1 year
      path = '/',
      domain,
      secure = window.location.protocol === 'https:',
      sameSite = 'strict'
    } = options

    const date = new Date()
    date.setTime(date.getTime() + (expires * 24 * 60 * 60 * 1000))

    let cookieString = `${encodeURIComponent(name)}=${encodeURIComponent(value)}`
    cookieString += `; expires=${date.toUTCString()}`
    cookieString += `; path=${path}`
    
    if (domain) {
      cookieString += `; domain=${domain}`
    }
    
    if (secure) {
      cookieString += '; secure'
    }
    
    cookieString += `; samesite=${sameSite}`

    document.cookie = cookieString
  }

  /**
   * Get a cookie value
   */
  static get(name: string): string | null {
    const nameEQ = encodeURIComponent(name) + '='
    const cookies = document.cookie.split(';')
    
    for (let cookie of cookies) {
      cookie = cookie.trim()
      if (cookie.indexOf(nameEQ) === 0) {
        return decodeURIComponent(cookie.substring(nameEQ.length))
      }
    }
    
    return null
  }

  /**
   * Delete a cookie
   */
  static delete(name: string, path: string = '/') {
    document.cookie = `${encodeURIComponent(name)}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path}`
  }

  /**
   * Check if cookies are enabled
   */
  static isEnabled(): boolean {
    // Try to set a test cookie
    const testKey = '_cookie_test_'
    const testValue = 'test'
    
    this.set(testKey, testValue, { expires: 1 })
    const result = this.get(testKey) === testValue
    this.delete(testKey)
    
    return result
  }

  /**
   * Get current consent status
   */
  static getConsent(): CookieConsent | null {
    const consent = this.get(CONSENT_COOKIE_NAME)
    if (!consent) return null
    
    try {
      return JSON.parse(consent)
    } catch {
      return null
    }
  }

  /**
   * Save consent preferences
   */
  static saveConsent(analytics: boolean, functional: boolean) {
    const consent: CookieConsent = {
      necessary: true,
      analytics,
      functional,
      timestamp: new Date().toISOString(),
      version: CONSENT_VERSION
    }
    
    this.set(CONSENT_COOKIE_NAME, JSON.stringify(consent), {
      expires: 365, // 1 year
      sameSite: 'strict'
    })
    
    // Apply consent by removing non-consented cookies
    this.applyConsent(consent)
  }

  /**
   * Apply consent by removing non-consented cookies
   */
  static applyConsent(consent: CookieConsent) {
    const allCookies = document.cookie.split(';')
    
    allCookies.forEach(cookie => {
      const [name] = cookie.split('=').map(c => c.trim())
      
      // Don't delete necessary cookies
      const necessaryCookies = [
        CONSENT_COOKIE_NAME,
        'session_*',
        'auth_*',
        'csrf_*'
      ]
      
      const isNecessary = necessaryCookies.some(pattern => {
        if (pattern.includes('*')) {
          const regex = new RegExp('^' + pattern.replace('*', '.*') + '$')
          return regex.test(name)
        }
        return name === pattern
      })
      
      if (isNecessary) return
      
      // Check if cookie type is consented
      const analyticsCookies = ['_ga', '_gid', '_gat', 'sentry_*']
      const functionalCookies = ['language', 'theme', 'preferences_*']
      
      const isAnalytics = analyticsCookies.some(pattern => {
        if (pattern.includes('*')) {
          const regex = new RegExp('^' + pattern.replace('*', '.*') + '$')
          return regex.test(name)
        }
        return name === pattern
      })
      
      const isFunctional = functionalCookies.some(pattern => {
        if (pattern.includes('*')) {
          const regex = new RegExp('^' + pattern.replace('*', '.*') + '$')
          return regex.test(name)
        }
        return name === pattern
      })
      
      // Delete non-consented cookies
      if ((isAnalytics && !consent.analytics) || (isFunctional && !consent.functional)) {
        this.delete(name)
      }
    })
  }

  /**
   * Check if a specific cookie type is consented
   */
  static hasConsent(type: 'necessary' | 'analytics' | 'functional'): boolean {
    const consent = this.getConsent()
    if (!consent) return false
    return consent[type]
  }

  /**
   * Revoke all consent and clear cookies
   */
  static revokeConsent() {
    // Get all cookies
    const allCookies = document.cookie.split(';')
    
    // Delete all cookies except necessary ones
    allCookies.forEach(cookie => {
      const [name] = cookie.split('=').map(c => c.trim())
      
      // Keep only the consent cookie to remember the revocation
      if (name !== CONSENT_COOKIE_NAME) {
        this.delete(name)
      }
    })
    
    // Save revoked consent
    this.saveConsent(false, false)
  }
}